// Bot/automation detection — heuristics shared across Behavio frontend apps.
// See @apps/bot-detectors. Issue #852.
import {
  isBot,
  isWebDriver,
  isHeadlessChrome,
  hasBotUserAgent,
  hasAutomationGlobals,
  hasChromiumMismatch,
  hasEmptyLanguages,
  bot,
} from "@apps/bot-detectors";

export {
  isBot,
  isWebDriver,
  isHeadlessChrome,
  hasBotUserAgent,
  hasAutomationGlobals,
  hasChromiumMismatch,
  hasEmptyLanguages,
  bot,
};

// Browser has touch screen
export const hasTouchScreen =
  "ontouchstart" in window ||
  // eslint-disable-next-line no-undef
  (window.DocumentTouch && document instanceof DocumentTouch) ||
  !!(window.navigator.msPointerEnabled && window.MSGesture);

// Firefox (Gecko) — desktop and Android. Firefox on iOS (`FxiOS`) is
// deliberately excluded: it runs on WebKit, not Gecko.
// The previous `typeof InstallTrigger !== "undefined"` check is dead — Firefox
// removed `InstallTrigger` in version 128, so it always evaluated to false.
export const detectFirefox = (ua = navigator.userAgent) =>
  /Firefox\//.test(ua) && !/FxiOS/.test(ua);

export const isFirefox = detectFirefox();

// Chrome 1+
export const isChrome =
  !!window.chrome && (!!window.chrome.webstore || !!window.chrome.runtime);

// Chrome on iOS
export const isChromeOnIOS = /CriOS/.test(navigator.userAgent);

// Chromium / Blink engine detection
export const isChromiumBased = !!window.chrome;

// Internet Explorer 6-11

// The historical `/*@cc_on!@*/ false ||` prefix was a conditional-compilation
// trick for IE below 8; `document.documentMode` already covers IE 8-11, which
// is the whole range this app could ever meet.
export const isIE = !!document.documentMode;

// Old MS Edge (EdgeHtml) < 75
export const isOldEdge = /Edge/.test(navigator.userAgent);

// MS Edge Chromium
export const isEdge = /Edg\//.test(navigator.userAgent);

// Opera 8.0+
export const isOpera =
  // eslint-disable-next-line no-undef
  (!!window.opr && !!opr.addons) ||
  !!window.opera ||
  /OPR/.test(navigator.userAgent);

// Safari
// https://www.whatismybrowser.com/guides/the-latest-user-agent/macos
// https://www.geeksforgeeks.org/how-to-detect-the-user-browser-safari-chrome-ie-firefox-and-opera-using-javascript/
export const isSafari =
  /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);

// Browser on Android
export const onAndroid = /Android/.test(navigator.userAgent);

// Browser on iPadOS 13 and later
// https://stackoverflow.com/questions/57776001/how-to-detect-ipad-pro-as-ipad-using-javascript
export const onIPadOS =
  /Macintosh/.test(navigator.userAgent) && "ontouchend" in document;

// Browser on iOS
export const onIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || onIPadOS;

// Browser on ChromeOS - Chromebook
export const onChromeOS = /CrOS/.test(navigator.userAgent);

// Browser on Windows
export const onWindows = /Win/.test(navigator.userAgent);

// Browser on Mac OS
// https://www.whatismybrowser.com/guides/the-latest-user-agent/macos
export const onMacOS =
  /Mac OS/.test(navigator.userAgent) && !("ontouchend" in document);

// Browser on BlackBerry
export const onBlackBerry = /BlackBerry/.test(navigator.userAgent);

/**
 * Checks if the application is running in a production environment.
 * This is determined by the `NODE_ENV` environment variable.
 */
// Node globals are scoped to build tooling, so `process` is undeclared here.
// Vite statically replaces this exact expression at build time, so it resolves
// in the browser; migrating to `import.meta.env.PROD` changes behaviour and is
// left for its own change.
// eslint-disable-next-line no-undef
export const onProdEnv = process.env.NODE_ENV === "production";

// Browser on mobile device
// https://stackoverflow.com/questions/11381673/detecting-a-mobile-browser
// https://developer.mozilla.org/en-US/docs/Web/API/NavigatorUAData/mobile
export const isMobile =
  (navigator.userAgentData
    ? navigator.userAgentData.mobile
    : onAndroid || onIOS || onBlackBerry) ||
  // same as breakpoint-sm in Tailwind CSS
  window.matchMedia("(max-width: 600px)").matches;

// Application is running in standalone mode as a PWA
// https://stackoverflow.com/questions/41742390/javascript-to-check-if-pwa-or-mobile-web
export const isPWA =
  navigator.standalone ||
  window.matchMedia("(display-mode: standalone)").matches;

// Client object with all the information
export const client = {
  userAgent: navigator.userAgent,
  userAgentData: navigator.userAgentData,
  // No `connection`: its values are prototype getters, so it saved as `{}`.
  // A result reads it fresh through toResultClient (#1376).
  maxTouchPoints: navigator.maxTouchPoints,
  hardwareConcurrency: navigator.hardwareConcurrency,
  deviceMemory: navigator.deviceMemory,
  cookieEnabled: navigator.cookieEnabled,
  doNotTrack: navigator.doNotTrack,
  language: navigator.language,
  languages: navigator.languages,
  resolution: {
    width: window.screen.width,
    height: window.screen.height,
    // https://whatismyviewport.com
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
  },
  browser: {
    isFirefox,
    isChrome,
    isChromeOnIOS,
    isChromiumBased,
    isIE,
    isOldEdge,
    isEdge,
    isOpera,
    isSafari,
    onAndroid,
    onIOS,
    onChromeOS,
    onWindows,
    onMacOS,
    onBlackBerry,
  },
  device: {
    hasTouchScreen,
    isMobile,
  },
  bot,
};
